import { useState, useEffect, useRef, useContext } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Loader from 'react-loader-spinner'
import Bugsnag from '@bugsnag/js'

import { authContext } from 'contexts/AuthContext'
import { ProfileContext } from 'contexts/ProfileContext'
import { NotificationContext } from 'contexts/NotificationContext'
import { notificationRead } from 'services/api/notification'
import { clearCookies } from 'services/helper/clearCookies'
import { signout } from 'services/api/authentication'

import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import ButtonBase from '@mui/material/ButtonBase'
import Grow from '@mui/material/Grow'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Badge from '@mui/material/Badge'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SearchIcon from '@mui/icons-material/Search'
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person'

import AdnexioWhite from 'assets/logos/adnexio-logo-white-v2-aug.png'
import AdnexioEduV2 from 'assets/logos/adnexio-logo-edu-v2-aug.png'
import Apple from 'assets/images/app-store-badge.png'
import Android from 'assets/images/google-play-badge.png'
import DefaultProfile from 'assets/images/jobseeker.jpg'
import PosterOne from '../assets/poster_one.png'
import SettingsModal from '../../SettingsModal'
import DeleteAccount from 'pages/profile/components/DeleteAccount'
import Flyout from 'components/smart/Header/components/Menu'
import ProfilerPanel from 'pages/personality_test/components/ProfilerPanel'

const GOOGLE_PLAY = process.env.REACT_APP_GOOGLE_PLAY_URL
const APP_STORE = process.env.REACT_APP_APPLE_STORE_URL
const ER_URL = process.env.REACT_APP_ER_URL

const mui = {
  menuIconBtn: {
    color: 'white',
    '&:focus': {
      outline: 'none',
    },
  },
  menuIcon: {
    fontSize: '30px',
  },
  notiCloseIconBtn: {
    color: 'white',
    '&:focus': {
      outline: 'none',
    },
  },
  notiCloseIcon: {
    color: 'white',
  },
  notiIconBtn: {
    color: 'white',
    '&:focus': {
      outline: 'none',
    },
    padding: '5px',
    marginRight: '2px',
    '@media (min-width:1024px)': {
      marginRight: '20px',
    },
  },
  notiIcon: {
    fontSize: '25px',
    transform: 'rotate(15deg)',
    '@media (min-width:1024px)': {
      fontSize: '30px',
    },
  },
  imgIconBtn: {
    color: 'white',
    '&:focus': {
      outline: 'none',
    },
    padding: '5px',
  },
  loginBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: '600',
    background: '#FCD94E',
    color: '#1B8379',
    borderRadius: '5px',
    boxShadow: 'none',
    padding: '5px 20px',
    '&:hover': {
      background: '#FCD94E',
      boxShadow: 'none',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  interviewnBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: '500',
    background: '#FCD94E',
    color: '#1B8379',
    boxShadow: 'none',
    padding: '5px 20px',
    '&:hover': {
      background: '#FCD94E',
      boxShadow: 'none',
    },
  },
  signUpBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 'normal',
    color: '#FFFFFF',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  onFocusButton: {
    color: '#FCD94E',
    fontWeight: '500',
  },
  upskillBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 'normal',
    color: '#F6DC74',
    backgroundColor: '#228E84',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  searchBar: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    color: '#959595',
  },
  searchIcon: {
    color: '#959595',
    fontSize: '27px',
  },
  mobileMenuBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 'normal',
    color: '#FFFFFF',
    width: '100%',
    display: 'flex',
    justifyContent: 'end',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  appBtn: {
    outline: 'none',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  notiBtn: {
    textAlign: 'left',
    fontWeight: '300',
    outline: 'none',
    width: '100%',
    padding: '10px 0px',
    justifyContent: 'flex-start',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&:hover': {
      backgroundColor: '#F9F9F9',
    },
  },
  badgeCustom: {
    '& .MuiBadge-badge': {
      '@media (max-width:1024px)': {
        padding: '0px',
        fontSize: '10px',
      },
    },
  },
  settingsBtn: {
    fontSize: '14px !important',
    padding: '15px 40px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  deleteBtn: {
    fontSize: '14px !important',
    padding: '15px 40px',
    color: 'red',
    fontWeight: '600',
    display: 'flex',
    justifyContent: 'space-between',
  },
  closeBtn: {
    color: 'white',
  },
  popper: {
    '& .MuiList-padding': {
      padding: '10px 10px 0px 10px',
    },
  },
  imageBtn: {
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: '400',
    fontSize: '13px',
  },
  enrollBtn: {
    fontFamily: 'Poppins',
    fontSize: '20px',
    textTransform: 'none',
    fontWeight: '600',
    background: '#FCD94E',
    color: '#1B8379',
    borderRadius: '11px',
    boxShadow: 'none',
    padding: '9px 45px',
    '&:hover': {
      background: '#FCD94E',
      boxShadow: 'none',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    marginTop: '56px',
  },
  popperTwo: {
    '& .MuiList-padding': {
      padding: '10px 10px 10px 10px',
    },
  },
  settingsEDUBtn: {
    fontSize: '14px !important',
    color: '#25292e',
  },
  deleteEDUBtn: {
    fontSize: '14px !important',
    color: '#cb2424',
  },
  CoursesBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 'normal',
    color: '#262626',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  signUpEduBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: 'normal',
    color: '#1B8379',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  loginEduBtn: {
    fontFamily: 'Poppins',
    fontSize: '14px',
    textTransform: 'none',
    fontWeight: '600',
    background: '#1B8379',
    color: '#FFFFFF',
    borderRadius: '5px',
    boxShadow: 'none',
    padding: '5px 20px',
    '&:hover': {
      background: '#FFFFFF',
      boxShadow: 'none',
      color: '#1B8379',
    },
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
}

const Header = ({ page, loginMode }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { pathname } = location
  const [openMobileMenu, setOpenMobileMenu] = useState(false)
  const [loading, setLoading] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)

  const { auth, logoutUser } = useContext(authContext)
  const { profileData } = useContext(ProfileContext)
  const { notificationData, getNotification } = useContext(NotificationContext)

  let isLoggedIn = auth.isAuth
  let profilePhoto = profileData !== null && profileData.profile_image_url
  let score = auth.score

  ///////////////////////////////////////////////////////////////// notification logic
  const [openNotification, setOpenNotification] = useState(false)
  const [openProfile, setOpenProfile] = useState(false)
  const anchorRefNotification = useRef(null)
  const anchorRefProfile = useRef(null)

  const handleToggleNotification = () => {
    setOpenNotification((prevOpen) => !prevOpen)
    setOpenMobileMenu(false)
  }

  const handleCloseNotification = (event) => {
    if (anchorRefNotification.current && anchorRefNotification.current.contains(event.target)) {
      return
    }
    setOpenNotification(false)
  }

  const onRedirectToSignUp = () => {
    if (pathname === '/bootcamp' || pathname === '/courses') {
      navigate('/signup', { state: { from: 'edu' } })
    } else {
      navigate('/signup')
    }
  }

  const onRedirectToUpskill = () => {
    if (pathname === '/bootcamp' || pathname === '/courses') {
      navigate('/signin', { state: { from: 'edu' } })
    } else {
      navigate('/signin')
    }
  }

  function handleListKeyDownNotification(event) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpenNotification(false)
    }
  }

  // return focus to the button when we transitioned from !openMobileMenu -> openMobileMenu
  const prevOpen = useRef(openNotification)
  useEffect(() => {
    if (prevOpen.current === true && openNotification === false) {
      anchorRefNotification.current.focus()
    }

    prevOpen.current = openNotification
  }, [openNotification])

  // mark as read notification
  const handleReadMessage = async (id) => {
    try {
      await notificationRead(id)
      await getNotification()
    } catch (e) {
      console.log(e)
    }
  }
  ///////////////////////////////////////////////////////////////// end notification logic

  // change bottom header height when open menu in mobile
  let bottomHeaderHeight = openMobileMenu ? 'h-36' : ''

  // change header style in track page
  let trackPageStyling = page === 'track' ? 'lg:justify-start' : 'lg:justify-around'

  // change header style in job page
  let jobPageStyling = page === 'jobs' && isLoggedIn ? 'flex items-center justify-center' : ''

  let disableBottomStyling =
    page === 'disableBottom' || page === 'courses' || page === 'my-classes'
      ? 'pt-0 pb-0'
      : 'pt-3 pb-6'

  let disableBottomStylingContainer =
    page === 'disableBottom' || page === 'courses' || page === 'my-classes' ? '' : 'lg:p-10'

  let disableBottomTopHeaderStyling =
    (page === 'disableBottom' || page === 'courses' || page === 'my-classes') && !openMobileMenu
      ? ''
      : ''

  // disable scroll when mobile menu open
  const handleMenu = () => {
    if (openMobileMenu) {
      setOpenMobileMenu(false)
      document.body.style.overflow = 'unset'
    } else if (!openMobileMenu) {
      setOpenMobileMenu(true)
    }
  }

  ///////////////////////////////////////////////////////////////// settings logic
  const [openSetting, setOpenSetting] = useState(false)

  const handleSettings = () => {
    setOpenSetting(true)
    setOpenMobileMenu(false)
  }
  ///////////////////////////////////////////////////////////////// end settings logic

  // reset mobile menu button if window resize
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1024) {
        setOpenMobileMenu(false)
        document.body.style.overflow = 'unset'
      }
    }
    window.addEventListener('resize', handleResize)
  })

  const handleRedirectProfile = () => {
    setOpenProfile(false)
    navigate('/profile')
  }

  const handleSignOut = async () => {
    try {
      setLoading(true)
      setOpenProfile(false)
      await signout()
      clearCookies()
      logoutUser()
      setLoading(false)
      navigate('/')
    } catch (e) {
      setLoading(false)
      Bugsnag.notify(e)
    }
  }

  const handleDismissProfileModal = (event) => {
    if (anchorRefProfile.current && anchorRefProfile.current.contains(event.target)) {
      return
    }
    setOpenProfile(false)
  }

  return (
    <>
      {loading === true && (
        <>
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: '999999999',
            }}
          >
            <Loader type="Oval" color="#09b7b7" height={55} width={55} />
          </div>
          <div
            style={{
              position: 'fixed',
              zIndex: '999999',
              width: '100vw',
              height: '100vh',
              background: 'black',
              opacity: '0.7',
            }}
          ></div>
        </>
      )}

      {/* Top Header */}
      <div
        className={`relative z-30 flex items-center justify-between ${
          page === 'edu' ? `bg-adx-white` : `bg-adx-darkGreen`
        } px-2 py-2 font-poppins lg:py-4 lg:px-14 ${disableBottomTopHeaderStyling}`}
      >
        {/* lg:min-h-full  */}
        {loginMode && (
          <>
            <div className="lg:hidden">
              {!openMobileMenu && (
                <IconButton sx={mui.menuIconBtn} onClick={handleMenu} size="large">
                  <MenuRoundedIcon sx={mui.menuIcon} />
                </IconButton>
              )}

              {openMobileMenu && (
                <IconButton sx={mui.menuIconBtn} onClick={handleMenu} size="large">
                  <CloseRoundedIcon sx={mui.menuIcon} />
                </IconButton>
              )}
            </div>
            <div className="absolute transform -translate-x-1/2 left-1/2">
              <Link
                to={page !== 'edu' && page !== 'courses' ? '/' : '/bootcamp'}
                className="cursor-pointer"
              >
                <img src={AdnexioWhite} className="w-36 lg:w-40" alt="logo" />
              </Link>
            </div>
          </>
        )}

        {loginMode === undefined && (
          <>
            <div className="lg:hidden">
              {!openMobileMenu && (
                <IconButton sx={mui.menuIconBtn} onClick={handleMenu} size="large">
                  <MenuRoundedIcon sx={mui.menuIcon} />
                </IconButton>
              )}

              {openMobileMenu && (
                <IconButton sx={mui.menuIconBtn} onClick={handleMenu} size="large">
                  <CloseRoundedIcon sx={mui.menuIcon} />
                </IconButton>
              )}
            </div>

            <div className="items-center space-x-12 lg:flex">
              <Link
                to={
                  page !== 'edu' && page !== 'courses' && page !== 'my-classes' ? '/' : '/bootcamp'
                }
                className="flex -mr-4 cursor-pointer"
              >
                {page === 'edu' || page === 'courses' || page === 'my-classes' ? (
                  <img src={AdnexioEduV2} className="w-32 lg:w-48" alt="logo" />
                ) : (
                  <img src={AdnexioWhite} className="w-36 lg:w-40" alt="logo" />
                )}
              </Link>
              {page !== 'courses' && page !== 'edu' && page !== 'my-classes' && (
                <div className="hidden mr-4 lg:block">
                  <Link to="/jobs">
                    <Button sx={mui.signUpBtn}>All Jobs</Button>
                  </Link>
                </div>
              )}

              {!isLoggedIn && page !== 'courses' && page !== 'edu' && (
                <>
                  <div className="hidden mr-4 lg:block">
                    <Button href={ER_URL} sx={mui.signUpBtn}>
                      For Employer
                    </Button>
                  </div>
                  <div className="hidden mr-4 lg:block">
                    <Flyout panelComponent={<ProfilerPanel />}>Professional profiler</Flyout>
                  </div>
                  <div className="hidden mr-4 lg:block">
                    <Button href="/gigs" sx={mui.signUpBtn}>
                      Gigs
                    </Button>
                  </div>
                  <div className="hidden mr-4 lg:block">
                    <Link to="/bootcamp" target="_blank">
                      <Button sx={mui.upskillBtn}>EDU</Button>
                    </Link>
                  </div>
                </>
              )}

              {!isLoggedIn && (page === 'courses' || page === 'edu') && (
                <div className="flex items-center space-x-12">
                  <div className="hidden lg:block">
                    <Link to="/courses">
                      <Button
                        sx={
                          pathname === '/courses'
                            ? { ...mui.CoursesBtn, ...mui.onFocusButton }
                            : mui.CoursesBtn
                        }
                      >
                        Courses
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {isLoggedIn && page !== 'courses' && page !== 'edu' && page !== 'my-classes' && (
                <>
                  <div className="hidden mr-4 lg:block">
                    <Link to="/track">
                      <Button sx={mui.signUpBtn}>Track</Button>
                    </Link>
                  </div>
                  <div className="hidden mr-4 lg:block">
                    <Flyout panelComponent={<ProfilerPanel />}>Professional profiler</Flyout>
                  </div>
                  <div className="hidden mr-4 lg:block">
                    <Link to="/gigs">
                      <Button sx={mui.signUpBtn}>Gigs</Button>
                    </Link>
                  </div>
                  <div className="hidden mr-4 lg:block">
                    <Link to="/bootcamp" target="_blank">
                      <Button sx={mui.upskillBtn}>EDU</Button>
                    </Link>
                  </div>
                </>
              )}

              {isLoggedIn && (page === 'courses' || page === 'edu' || page === 'my-classes') && (
                <div className="flex items-center space-x-12">
                  <div
                    className="flex items-center px-4 space-x-12 rounded-full"
                    style={{ backgroundColor: '#228E84' }}
                  >
                    <div className="hidden mr-4 lg:block ">
                      <Link to="/my-classes">
                        <Button
                          sx={
                            pathname === '/my-classes' || pathname === '/upskill-payment'
                              ? { ...mui.signUpBtn, ...mui.onFocusButton }
                              : { ...mui.signUpBtn }
                          }
                        >
                          My Dashboard
                        </Button>
                      </Link>
                    </div>
                    <div className="hidden mr-4 lg:block">
                      <Link to="/courses">
                        <Button
                          sx={
                            pathname === '/courses'
                              ? { ...mui.signUpBtn, ...mui.onFocusButton }
                              : { ...mui.signUpBtn }
                          }
                        >
                          Courses
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center mr-2">
              {!isLoggedIn && (
                <>
                  <div className="hidden mr-4 lg:block">
                    <Button
                      sx={page === 'edu' ? mui.signUpEduBtn : mui.signUpBtn}
                      onClick={onRedirectToSignUp}
                    >
                      Sign Up
                    </Button>
                  </div>
                  <div>
                    <Button
                      onClick={onRedirectToUpskill}
                      variant="contained"
                      sx={page === 'edu' ? mui.loginEduBtn : mui.loginBtn}
                    >
                      Sign In
                    </Button>
                  </div>
                </>
              )}

              {isLoggedIn && (
                <>
                  <IconButton
                    sx={mui.notiIconBtn}
                    onClick={handleToggleNotification}
                    ref={anchorRefNotification}
                    size="large"
                  >
                    <Badge
                      badgeContent={notificationData !== null && notificationData.total}
                      color="error"
                      max={99}
                      sx={mui.badgeCustom}
                    >
                      <NotificationsRoundedIcon sx={mui.notiIcon} />
                    </Badge>
                  </IconButton>
                  <Popper
                    open={openNotification}
                    anchorEl={anchorRefNotification.current}
                    transition
                    disablePortal
                    placement="bottom-end"
                    className="z-50 w-10/12 max-w-sm lg:w-auto lg:min-w-400 lg:max-w-sm"
                    sx={mui.popper}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
                        <div className="pb-4 text-xs rounded-lg shadow-md outline-none bg-adx-white font-poppins">
                          <ClickAwayListener onClickAway={handleCloseNotification}>
                            <MenuList
                              autoFocusItem={openNotification}
                              onKeyDown={handleListKeyDownNotification}
                            >
                              <div className="flex items-center justify-between px-4 mb-2 rounded-lg outline-none bg-adx-darkGreen">
                                <div className="text-base font-medium outline-none text-adx-white">
                                  Notifications
                                </div>
                                <IconButton
                                  sx={mui.notiCloseIconBtn}
                                  onClick={handleCloseNotification}
                                  size="large"
                                >
                                  <CloseRoundedIcon fontSize="small" sx={mui.notiCloseIcon} />
                                </IconButton>
                              </div>
                              {notificationData !== null && notificationData.total === 0 && (
                                <div className="flex items-center justify-center px-4 pt-2 overflow-y-scroll font-light lg:no-scrollbar max-h-80 min-h-200 lg:overflow-y-auto lg:text-sm">
                                  <div className="font-light">You are up to date</div>
                                </div>
                              )}
                              {notificationData !== null && notificationData.total !== 0 && (
                                <div className="px-4 pt-2 overflow-y-scroll font-light lg:no-scrollbar max-h-80 min-h-200 lg:overflow-y-auto lg:text-sm">
                                  {notificationData.data.map((item, index) => (
                                    <div key={item.id}>
                                      <ButtonBase
                                        sx={mui.notiBtn}
                                        onClick={() => handleReadMessage(item.id)}
                                      >
                                        {item.photo_url !== null && (
                                          <img
                                            src={item.photo_url}
                                            alt="profile"
                                            className="object-cover w-8 h-8 mr-3 rounded-full shadow-md lg:h-12 lg:w-12"
                                          />
                                        )}
                                        {item.photo_url === null && (
                                          <img
                                            src={AdnexioWhite}
                                            alt="profile"
                                            className="object-contain w-8 h-8 mr-3 rounded-full shadow-md bg-adx-darkGreen lg:h-12 lg:w-12"
                                          />
                                        )}
                                        <div className="flex flex-col">
                                          <div className="text-xs font-semibold">{item.title}</div>
                                          <div className="text-xs">
                                            {typeof item.message === 'string' && item.message}
                                          </div>
                                        </div>
                                      </ButtonBase>
                                      {notificationData.data.length !== index + 1 && <hr />}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </MenuList>
                          </ClickAwayListener>
                        </div>
                      </Grow>
                    )}
                  </Popper>

                  <IconButton
                    sx={mui.imgIconBtn}
                    onClick={() => {
                      setOpenProfile((prevOpen) => !prevOpen)
                    }}
                    ref={anchorRefProfile}
                    size="large"
                  >
                    {profileData !== null && (
                      <>
                        {profileData.profile_image_url !== null &&
                          profileData.profile_image_url !== undefined && (
                            <img
                              src={profilePhoto}
                              alt="profile_image"
                              className="object-cover w-8 h-8 rounded-full lg:h-12 lg:w-12"
                            />
                          )}
                        {(profileData.profile_image_url === null ||
                          profileData.profile_image_url === undefined) && (
                          <img
                            src={DefaultProfile}
                            alt="profile_image"
                            className="object-cover w-8 h-8 rounded-full lg:h-12 lg:w-12"
                          />
                        )}
                      </>
                    )}
                  </IconButton>
                  <Popper
                    open={openProfile}
                    anchorEl={anchorRefProfile.current}
                    transition
                    disablePortal
                    placement="bottom-end"
                    className="z-50 w-4/12 max-w-sm min-w-min lg:w-auto lg:max-w-sm"
                    sx={mui.popperTwo}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
                        <div className="min-w-[150px] max-w-[300px] rounded-lg bg-white shadow">
                          <ClickAwayListener onClickAway={handleDismissProfileModal}>
                            <MenuList autoFocusItem={openProfile}>
                              <div className="mb-2 space-y-1 border-b px-4 pb-3 pt-1 text-sm text-[#607d8b]">
                                <p className="truncate">{profileData.name}</p>
                                <p className="text-xs truncate">{profileData.email}</p>
                              </div>
                              <MenuItem onClick={handleRedirectProfile} sx={mui.settingsEDUBtn}>
                                <PersonIcon
                                  sx={{ marginRight: '8px', fontSize: '22px', marginLeft: '-3px' }}
                                />
                                Profile
                              </MenuItem>
                              <MenuItem onClick={handleSignOut} sx={mui.deleteEDUBtn}>
                                <LogoutIcon sx={{ marginRight: '8px', fontSize: '20px' }} /> Sign
                                out
                              </MenuItem>
                            </MenuList>
                          </ClickAwayListener>
                        </div>
                      </Grow>
                    )}
                  </Popper>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom Header */}
      <div
        className={`-mt-px h-auto bg-adx-darkGreen font-poppins ${disableBottomStylingContainer}`}
      >
        <div
          className={`relative mx-auto max-w-7xl px-6 ${disableBottomStyling} ${bottomHeaderHeight} lg:flex lg:items-center ${trackPageStyling} ${jobPageStyling}`}
        >
          {openMobileMenu && !isLoggedIn && page !== 'edu' && page !== 'courses' && (
            <div className="absolute top-0 left-0 z-30 w-full bg-adx-darkGreen lg:hidden">
              <div className="flex flex-col items-start px-6 pt-2 pb-8 space-y-2">
                <Link to="/jobs" className="w-full">
                  <Button sx={mui.mobileMenuBtn}>All Jobs</Button>
                </Link>
                <Link to="/signup" className="w-full">
                  <Button sx={mui.mobileMenuBtn}>Join Us</Button>
                </Link>
                <Link to="/gigs" className="w-full">
                  <Button sx={mui.mobileMenuBtn}>Gigs</Button>
                </Link>
                <Link to="/bootcamp" className="w-full" target="_blank">
                  <Button sx={mui.mobileMenuBtn}>EDU</Button>
                </Link>
                <Button href={ER_URL} sx={mui.mobileMenuBtn}>
                  For Employers
                </Button>
              </div>
            </div>
          )}

          {openMobileMenu && !isLoggedIn && (page === 'edu' || page === 'courses') && (
            <div className="absolute top-0 left-0 z-30 w-full bg-adx-darkGreen lg:hidden">
              <div className="flex flex-col items-start px-6 pt-2 pb-8 space-y-2">
                <Link to="/courses" className="w-full">
                  <Button sx={mui.mobileMenuBtn}>Courses</Button>
                </Link>
              </div>
            </div>
          )}

          {openMobileMenu &&
            isLoggedIn &&
            page !== 'edu' &&
            page !== 'courses' &&
            page !== 'my-classes' && (
              <div className="absolute top-0 left-0 z-30 w-full bg-adx-darkGreen lg:hidden">
                <div className="flex flex-col items-start px-6 pt-2 pb-6 space-y-2">
                  <Link to="/jobs" className="w-full">
                    <Button sx={mui.mobileMenuBtn}>All Jobs</Button>
                  </Link>
                  <Link to="/track" className="w-full">
                    <Button sx={mui.mobileMenuBtn}>Track</Button>
                  </Link>
                  <Link to="/personality-test" className="w-full">
                    <Button sx={mui.mobileMenuBtn}>Personality Test</Button>
                  </Link>
                  <Link to="/video-interview" className="w-full">
                    <Button sx={mui.mobileMenuBtn}>Video Interview</Button>
                  </Link>
                  <Link to="/gigs" className="w-full">
                    <Button sx={mui.mobileMenuBtn}>Gigs</Button>
                  </Link>
                  <Link to="/bootcamp" className="w-full" target="_blank">
                    <Button sx={mui.mobileMenuBtn}>EDU</Button>
                  </Link>
                  <Button sx={mui.mobileMenuBtn} onClick={handleSettings}>
                    Settings
                  </Button>
                </div>
              </div>
            )}

          {openMobileMenu &&
            isLoggedIn &&
            (page === 'edu' || page === 'courses' || page === 'my-classes') && (
              <div className="absolute top-0 left-0 z-30 w-full bg-adx-darkGreen lg:hidden">
                <div className="flex flex-col items-start px-6 pt-2 pb-6 space-y-2">
                  <Link to="/my-classes" className="w-full">
                    <Button sx={mui.mobileMenuBtn}>My Dashboard</Button>
                  </Link>
                  <Link to="/courses" className="w-full">
                    <Button sx={mui.mobileMenuBtn}>Courses</Button>
                  </Link>
                  <Button sx={mui.mobileMenuBtn} onClick={handleSettings}>
                    Settings
                  </Button>
                </div>
              </div>
            )}

          <SettingsModal
            openSetting={openSetting}
            setOpenSetting={setOpenSetting}
            loading={loading}
            setLoading={setLoading}
            setOpenDelete={setOpenDelete}
            openDelete={openDelete}
          />

          <DeleteAccount
            setOpenDelete={setOpenDelete}
            openDelete={openDelete}
            loading={loading}
            setLoading={setLoading}
          />

          {/* 1st Column */}
          {page === undefined && (
            <div className="max-w-2xl mx-auto lg:mx-0">
              <div className="text-lg font-semibold text-adx-white lg:text-4xl">
                A Better Platform For Better Jobs
              </div>
              <div className="text-sm font-extralight text-adx-white lg:mt-2 lg:text-lg">
                Find your dream job with our AI-based career guidance modules. The next phase of
                your career begins here.
              </div>
              <form
                className="p-1 mt-5 rounded-4xl bg-adx-white lg:mb-8 lg:max-w-md lg:py-2"
                method="GET"
                action="/jobs"
                autoComplete="off"
              >
                <SearchIcon className="mx-2" sx={mui.searchIcon} />
                <InputBase
                  className="w-80percent"
                  sx={mui.searchBar}
                  placeholder="Search"
                  name="search"
                />
              </form>

              <div className="hidden lg:flex lg:items-center lg:space-x-4">
                <div className="text-lg font-semibold text-adx-white">For a better experience</div>
                <ButtonBase sx={mui.appBtn} href={APP_STORE} target="_blank">
                  <div>
                    <img src={Apple} alt="apple" className="rounded-md w-28" />
                  </div>
                </ButtonBase>
                <ButtonBase sx={mui.appBtn} href={GOOGLE_PLAY} target="_blank">
                  <div>
                    <img src={Android} alt="android" className="rounded-md w-28" />
                  </div>
                </ButtonBase>
              </div>
            </div>
          )}

          {page === 'jobs' && !isLoggedIn && (
            <div className="max-w-lg mx-auto lg:mx-0">
              <div className="pl-3 text-lg font-semibold text-adx-white lg:text-2xl">
                Find a job <span className="font-extralight">you will love</span>
              </div>
              <form
                className="p-1 mt-5 rounded-4xl bg-adx-white lg:mb-0 lg:min-w-440 lg:max-w-md lg:py-2"
                method="GET"
                action="/jobs"
                autoComplete="off"
              >
                <SearchIcon className="mx-2" sx={mui.searchIcon} />
                <InputBase
                  className="w-80percent"
                  sx={mui.searchBar}
                  placeholder="Search"
                  name="search"
                />
              </form>
            </div>
          )}

          {page === 'jobs' && isLoggedIn && (
            <div className="max-w-lg mx-autop lg:mx-0">
              <div className="pl-3 text-base font-semibold text-adx-white lg:text-2xl">
                Video Interview
              </div>
              <div className="pl-3 mt-2 text-xs font-light text-adx-white lg:text-base">
                Complete a simple 5 minute interview with our AI Coach, NEX, to improve your score
                and get better job matches.
              </div>
              <div className="pl-3 mt-4">
                <Button variant="contained" sx={mui.interviewnBtn}>
                  <Link to="/video-interview">Start Interview</Link>
                </Button>
              </div>
            </div>
          )}

          {page === 'track' && (
            <div className="max-w-lg mx-auto lg:mx-0 lg:ml-10">
              <div className="pl-3 text-base font-semibold text-adx-white lg:text-2xl">Track</div>
              <div className="pl-3 mt-2 text-xs font-light text-adx-white lg:text-base">
                Keep tracks of your future jobs.
              </div>
            </div>
          )}

          {/* 2nd Column */}
          {page === undefined && (
            <div className="hidden lg:block">
              <img src={PosterOne} alt="poster_one" />
            </div>
          )}

          {page === 'jobs' && !isLoggedIn && (
            <div className="hidden lg:block">
              <div className="hidden lg:flex lg:flex-col lg:items-center lg:space-y-5">
                <div className="text-2xl font-semibold text-adx-white">For a better experience</div>
                <div className="space-x-4">
                  <ButtonBase sx={mui.appBtn} href={APP_STORE} target="_blank">
                    <div>
                      <img src={Apple} alt="apple" className="w-40 rounded-md" />
                    </div>
                  </ButtonBase>
                  <ButtonBase sx={mui.appBtn} href={GOOGLE_PLAY} target="_blank">
                    <div>
                      <img src={Android} alt="android" className="w-40 rounded-md" />
                    </div>
                  </ButtonBase>
                </div>
              </div>
            </div>
          )}

          {page === 'jobs' && isLoggedIn && (
            <div className="max-w-lg ml-6 lg:ml-0">
              <div className="flex items-center justify-center lg:space-x-10">
                <div className="hidden w-1/2 p-4 text-sm font-light text-center text-white rounded-xl bg-adx-lightGreen lg:block">
                  {`Your score is ${
                    score !== 0 ? score.toFixed(1) : score
                  }. Improve your score by downloading the adnexio mobile app for a better chance of getting hired`}
                  .
                </div>
                <div className="flex flex-col items-center justify-center w-24 h-24 text-white border-2 border-white rounded-full lg:h-32 lg:w-32 lg:border-4">
                  <div className="text-3xl font-bold lg:text-5xl">
                    {score !== 0 ? score.toFixed(1) : score}
                  </div>
                  <div className="font-light lg:text-xl">Score</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {openMobileMenu && (
          <div
            className="fixed top-0 left-0 z-20 w-screen h-screen bg-black opacity-70 lg:hidden"
            onClick={handleMenu}
          />
        )}
      </div>
    </>
  )
}

export default Header
